import {
  Box,
  Step,
  StepConnector,
  stepConnectorClasses,
  StepLabel,
  Stepper,
  Tooltip,
} from "@mui/material";
import { StepIconProps } from "@mui/material/StepIcon";
import { styled } from "@mui/material/styles";
import { QuestionCategory } from "database";
import AnimatedIcon from "ui/animate/AnimatedIcon";
import { useLocales } from "../../locales";

import careIcon from "../../assets/icons/lordicon/care.json";
import coachIcon from "../../assets/icons/lordicon/coach.json";
import deliverIcon from "../../assets/icons/lordicon/deliver.json";
import developIcon from "../../assets/icons/lordicon/develop.json";
import leadIcon from "../../assets/icons/lordicon/lead.json";
import { bgGradient } from "../../utils/cssStyles";

type Props = {
  steps: QuestionCategory[];
  activeStep?: number;
};

export default function CustomizedSteppers({ steps, activeStep }: Props) {
  const { translate } = useLocales();
  
  // Create tooltips with error handling
  let categoryTooltips: any = null;
  try {
    categoryTooltips = getCategoryTooltips(translate);
  } catch (error) {
    console.error("Error creating category tooltips:", error);
    categoryTooltips = null;
  }
  
  return (
    <>
      <Box sx={{ mb: 0 }} />

      <Stepper
        alternativeLabel
        activeStep={activeStep ?? steps.length}
        connector={<ColorlibConnector />}
      >
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel 
              StepIconComponent={(props) => (
                <ColorlibStepIcon 
                  {...props} 
                  categoryTooltips={categoryTooltips}
                  steps={steps}
                />
              )}
            >
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
    </>
  );
}

const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 22,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      ...bgGradient({
        startColor: theme.palette.error.light,
        endColor: theme.palette.error.main,
      }),
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      ...bgGradient({
        startColor: theme.palette.error.light,
        endColor: theme.palette.error.main,
      }),
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    borderRadius: 1,
    backgroundColor: theme.palette.divider,
  },
}));

export const ColorlibStepIconRoot = styled("div")<{
  ownerState: { completed?: boolean; active?: boolean };
}>(({ theme, ownerState }) => ({
  zIndex: 1,
  width: 50,
  height: 50,
  display: "flex",
  borderRadius: "50%",
  alignItems: "center",
  justifyContent: "center",
  color: theme.palette.text.disabled,
  backgroundColor:
    theme.palette.mode === "light"
      ? theme.palette.grey[300]
      : theme.palette.grey[700],
  ...(ownerState.active && {
    boxShadow: "0 4px 10px 0 rgba(0,0,0,.25)",
    color: theme.palette.common.white,
    background: theme.palette.primary.light,
  }),
  ...(ownerState.completed && {
    color: theme.palette.common.white,
    background: theme.palette.primary.main,
  }),
}));

function ColorlibStepIcon(props: StepIconProps & { categoryTooltips?: any; steps?: QuestionCategory[] }) {
  const { active, completed, className, icon, categoryTooltips, steps, ...rest } = props;

  // Map the numeric icon (1, 2, 3, etc.) to the actual category from steps array
  const categoryIndex = Number(icon) - 1; // Material-UI icons are 1-based, array is 0-based
  const category = steps && steps[categoryIndex];
  
  const iconElement = categoryTooltips && category ? categoryTooltips[category] : (category ? CATEGORY_ICONS[category] : null);

  return (
    <ColorlibStepIconRoot
      ownerState={{ completed, active }}
      className={className}
      sx={rest}
    >
      {iconElement}
    </ColorlibStepIconRoot>
  );
}

export const CATEGORY_ICONS: {
  [index in QuestionCategory]: React.ReactElement;
} = {
  [QuestionCategory.VISION]: <AnimatedIcon icon={coachIcon} animateOnHover />,
  [QuestionCategory.COMMUNICATION]: (
    <AnimatedIcon icon={deliverIcon} animateOnHover />
  ),
  [QuestionCategory.DEVELOPMENT]: (
    <AnimatedIcon icon={developIcon} animateOnHover />
  ),
  [QuestionCategory.TRUST]: <AnimatedIcon icon={careIcon} animateOnHover />,
  [QuestionCategory.PROBLEM_SOLVING]: (
    <AnimatedIcon icon={leadIcon} animateOnHover />
  ),
  [QuestionCategory.CARE]: <AnimatedIcon icon={careIcon} animateOnHover />,
  [QuestionCategory.COACH]: <AnimatedIcon icon={coachIcon} animateOnHover />,
  [QuestionCategory.DEVELOP]: (
    <AnimatedIcon icon={developIcon} animateOnHover />
  ),
  [QuestionCategory.DELIVER]: (
    <AnimatedIcon icon={deliverIcon} animateOnHover />
  ),
  [QuestionCategory.LEAD]: <AnimatedIcon icon={leadIcon} animateOnHover />,
};

export const getCategoryTooltips = (translate: any) => {
  try {
    return {
      [QuestionCategory.VISION]: (
        <Tooltip
          title={translate("categories.vision") as unknown as string}
          placement="top"
          arrow
        >
          <span>{CATEGORY_ICONS[QuestionCategory.VISION]}</span>
        </Tooltip>
      ),
      [QuestionCategory.COMMUNICATION]: (
        <Tooltip
          title={translate("categories.communication") as unknown as string}
          placement="top"
          arrow
        >
          <span>{CATEGORY_ICONS[QuestionCategory.COMMUNICATION]}</span>
        </Tooltip>
      ),
      [QuestionCategory.DEVELOPMENT]: (
        <Tooltip
          title={translate("categories.development") as unknown as string}
          placement="top"
          arrow
        >
          <span>{CATEGORY_ICONS[QuestionCategory.DEVELOPMENT]}</span>
        </Tooltip>
      ),
      [QuestionCategory.TRUST]: (
        <Tooltip
          title={translate("categories.trust") as unknown as string}
          placement="top"
          arrow
        >
          <span>{CATEGORY_ICONS[QuestionCategory.TRUST]}</span>
        </Tooltip>
      ),
      [QuestionCategory.PROBLEM_SOLVING]: (
        <Tooltip
          title={translate("categories.problem_solving") as unknown as string}
          placement="top"
          arrow
        >
          <span>{CATEGORY_ICONS[QuestionCategory.PROBLEM_SOLVING]}</span>
        </Tooltip>
      ),
      [QuestionCategory.CARE]: (
        <Tooltip
          title={translate("categories.care") as unknown as string}
          placement="top"
          arrow
        >
          <span>{CATEGORY_ICONS[QuestionCategory.CARE]}</span>
        </Tooltip>
      ),
      [QuestionCategory.COACH]: (
        <Tooltip
          title={translate("categories.coach") as unknown as string}
          placement="top"
          arrow
        >
          <span>{CATEGORY_ICONS[QuestionCategory.COACH]}</span>
        </Tooltip>
      ),
      [QuestionCategory.DEVELOP]: (
        <Tooltip
          title={translate("categories.develop") as unknown as string}
          placement="top"
          arrow
        >
          <span>{CATEGORY_ICONS[QuestionCategory.DEVELOP]}</span>
        </Tooltip>
      ),
      [QuestionCategory.DELIVER]: (
        <Tooltip
          title={translate("categories.deliver") as unknown as string}
          placement="top"
          arrow
        >
          <span>{CATEGORY_ICONS[QuestionCategory.DELIVER]}</span>
        </Tooltip>
      ),
      [QuestionCategory.LEAD]: (
        <Tooltip
          title={translate("categories.lead") as unknown as string}
          placement="top"
          arrow
        >
          <span>{CATEGORY_ICONS[QuestionCategory.LEAD]}</span>
        </Tooltip>
      ),
    };
  } catch (error) {
    console.error("Error in getCategoryTooltips:", error);
    // Return fallback with just icons
    return {
      [QuestionCategory.VISION]: CATEGORY_ICONS[QuestionCategory.VISION],
      [QuestionCategory.COMMUNICATION]: CATEGORY_ICONS[QuestionCategory.COMMUNICATION],
      [QuestionCategory.DEVELOPMENT]: CATEGORY_ICONS[QuestionCategory.DEVELOPMENT],
      [QuestionCategory.TRUST]: CATEGORY_ICONS[QuestionCategory.TRUST],
      [QuestionCategory.PROBLEM_SOLVING]: CATEGORY_ICONS[QuestionCategory.PROBLEM_SOLVING],
      [QuestionCategory.CARE]: CATEGORY_ICONS[QuestionCategory.CARE],
      [QuestionCategory.COACH]: CATEGORY_ICONS[QuestionCategory.COACH],
      [QuestionCategory.DEVELOP]: CATEGORY_ICONS[QuestionCategory.DEVELOP],
      [QuestionCategory.DELIVER]: CATEGORY_ICONS[QuestionCategory.DELIVER],
      [QuestionCategory.LEAD]: CATEGORY_ICONS[QuestionCategory.LEAD],
    };
  }
};

// This will be created dynamically in the component
